import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Users, Settings, Moon, Sun, LogOut, BarChart3, Receipt, Crown, CreditCard, NotebookPen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/BrandMark";

const tabs = [
  { to: "/clients", label: "Clients", icon: Users, id: "nav-clients-tab" },
  { to: "/drafts", label: "Drafts", icon: NotebookPen, id: "nav-drafts-tab" },
  { to: "/reports", label: "Reports", icon: BarChart3, id: "nav-reports-tab" },
  { to: "/expenses", label: "Expenses", icon: Receipt, id: "nav-expenses-tab" },
  { to: "/settings", label: "Settings", icon: Settings, id: "nav-settings-tab" },
];

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("di_theme") === "dark");
  const b = user?.billing || {};
  const allTabs = b.is_admin ? [...tabs, { to: "/admin", label: "Subscribers", icon: Crown, id: "nav-admin-tab" }] : tabs;

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("di_theme", dark ? "dark" : "light");
  }, [dark]);

  const linkCls = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
      isActive ? "bg-emerald-600 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-secondary"
    }`;

  return (
    <div className="min-h-screen pb-24 md:pb-10">
      <header className="sticky top-0 z-30 bg-card/90 backdrop-blur-md border-b border-border print:hidden">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark size={36} />
            <div className="min-w-0">
              <div className="font-heading font-bold leading-tight tracking-tight">Basta10</div>
              <div className="text-xs text-muted-foreground truncate" data-testid="nav-business-name">{user?.business_name}</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {allTabs.map((t) => (
              <NavLink key={t.to} to={t.to} className={linkCls} data-testid={t.id}>
                <t.icon className="h-4 w-4" /> {t.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1">
            <button data-testid="nav-theme-toggle" onClick={() => setDark(!dark)}
              className="h-9 w-9 grid place-items-center rounded-lg hover:bg-secondary transition-colors" aria-label="Toggle theme">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button data-testid="auth-logout-button" onClick={() => logout().then(() => navigate("/login"))}
              className="h-9 w-9 grid place-items-center rounded-lg hover:bg-secondary transition-colors" aria-label="Log out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
        {(b.status === "trialing" || b.status === "trial") && b.trial_end && !b.is_admin && (
          <button data-testid="trial-banner" onClick={() => navigate("/billing")}
            className={`w-full mb-6 flex items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm text-left transition-colors print:hidden ${b.status === "trial" && daysLeft(b.trial_end) <= 3 ? "border-amber-300 bg-amber-50/70 dark:bg-amber-950/30 dark:border-amber-800 hover:bg-amber-100/70" : "border-blue-200 bg-blue-50/70 dark:bg-blue-950/30 dark:border-blue-800 hover:bg-blue-100/70"}`}>
            <span className="flex items-center gap-2 min-w-0"><CreditCard className="h-4 w-4 text-blue-600 shrink-0" /><span>Free trial — <b>{daysLeft(b.trial_end)} day{daysLeft(b.trial_end) === 1 ? "" : "s"}</b> left{b.status === "trial" ? <span className="hidden sm:inline"> · no card on file</span> : ""}</span></span>
            <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 shrink-0 whitespace-nowrap">{b.status === "trial" ? "Add a card →" : "Manage plan →"}</span>
          </button>
        )}
        {b.comp_until && !b.is_admin && b.status !== "active" && b.status !== "trialing" && b.status !== "trial" && (
          <div data-testid="comp-banner" className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50/70 dark:bg-emerald-950/30 dark:border-emerald-800 px-4 py-2.5 text-sm print:hidden">
            Complimentary access until <b>{new Date(b.comp_until).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</b>
          </div>
        )}
        <Outlet />
      </main>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-card border-t border-border grid grid-cols-5 print:hidden">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} data-testid={`${t.id}-mobile`}
            className={({ isActive }) => `flex flex-col items-center gap-1 py-3 text-[11px] font-semibold ${isActive ? "text-emerald-600" : "text-muted-foreground"}`}>
            <t.icon className="h-5 w-5" /> {t.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
